import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Android,
  Assessment,
  Groups,
  PhoneAndroid,
  Security,
  Category,
} from "@mui/icons-material";
import { PLAY_STORE_URL } from "../config/store";

const HowToUse = () => {
  const theme = useTheme();

  const steps = [
    {
      label: "Download Treasure",
      description:
        "Get Treasure from Google Play, install it, and open the app on your Android phone.",
      icon: PhoneAndroid,
    },
    {
      label: "Sign in with your phone",
      description:
        "Enter your mobile number and verify with the OTP. Your account is linked to your number for secure access.",
      icon: Security,
    },
    {
      label: "Add income and expenses",
      description:
        "Tap Add Transaction to record money coming in or going out. Choose income or expense, pick a category, enter amount and date, and add a remark if you like.",
      icon: Add,
    },
    {
      label: "Categorize your spending",
      description:
        "Use categories so food, travel, bills, and other spends stay organized. That makes it easier to see where your money goes.",
      icon: Category,
    },
    {
      label: "Check reports and your month",
      description:
        "Open Reports to see summaries, category breakdowns, and trends. On Home, review income, expenses, and remaining balance for the month.",
      icon: Assessment,
    },
    {
      label: "Optional: auction committees",
      description:
        "Treasure also includes auction committee management as an additional capability. From Menu, open Committees to create a committee or view committees you have been added to.",
      icon: Groups,
    },
  ];

  return (
    <Box sx={{ bgcolor: "#f7f5fb", pb: 8 }}>
      <Box
        sx={{
          color: "white",
          background: `linear-gradient(160deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, #7E57C2 100%)`,
          py: { xs: 6, md: 8 },
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              opacity: 0.95,
              mb: 1.5,
            }}
          >
            Getting started
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.9rem", md: "2.5rem" },
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
              mb: 1.5,
            }}
          >
            How to Use Treasure: Track Income, Expenses &amp; Reports
          </Typography>
          <Typography sx={{ opacity: 0.94, lineHeight: 1.7, maxWidth: 560, fontSize: "1.05rem", mb: 3 }}>
            A short guide to track income and expenses, understand your spending, and get value from Treasure quickly.
          </Typography>
          <Button
            component="a"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<Android />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 999,
              bgcolor: "white",
              color: "primary.main",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            Get Treasure on Google Play
          </Button>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: { xs: -3, md: -4 }, px: 2 }}>
        <Stack spacing={2}>
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Box
                key={step.label}
                sx={{
                  display: "flex",
                  gap: 2,
                  p: { xs: 2.5, md: 3 },
                  bgcolor: "white",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "rgba(94,53,177,0.12)",
                  boxShadow: "0 8px 24px rgba(94,53,177,0.06)",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      bgcolor: "primary.main",
                      color: "white",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 800,
                      color: "primary.main",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                </Box>
                <Box sx={{ pt: 0.5 }}>
                  <Typography fontWeight={800} sx={{ mb: 0.75 }}>
                    {step.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>

        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            bgcolor: "white",
            border: "1px solid",
            borderColor: "rgba(94,53,177,0.12)",
            textAlign: "center",
          }}
        >
          <Typography fontWeight={700} sx={{ mb: 1 }}>
            Need more help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            Reach out through Contact Us and we will get back to you.
          </Typography>
          <Button
            component={RouterLink}
            to="/contact-us"
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, mr: 1.5, mb: { xs: 1.5, sm: 0 } }}
          >
            Contact us
          </Button>
          <Button
            component={RouterLink}
            to="/blog"
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Read money guides
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HowToUse;
